import { Modal, Flex, theme, Typography, Button, Input, message } from 'antd'
import { useShareMoneyflowModal } from './useShareMoneyFlowModal'
import { useGetMoneyFlow } from '../../api/case/document'
import { useParams } from 'react-router'
import { FormEventHandler, useCallback, useContext, useEffect, useState } from 'react'
import { useGenerateShareableLinkMutation, useGetShareableLinkQuery } from '../../api/moneyflow/generateShareableLink'
import { ShareAltOutlined } from '@ant-design/icons'
import { DiagramViewPortContext } from '../../components/sankey/DiagramViewPortContext'
import html2canvas from 'html2canvas'
import { addBranding } from '../../utils/addBranding'
import { useDarkMode } from '../../hooks/useDarkMode'

export const ShareMoneyFlowModal = () => {
    const { documentId } = useParams()
    const { visible, hide } = useShareMoneyflowModal()
    const { htmlContainer } = useContext(DiagramViewPortContext)
    const { data: moneyflow, refetch } = useGetMoneyFlow(documentId)
    const [name, setName] = useState(moneyflow?.name ?? '')
    const [previewImage, setPreviewImage] = useState<File | null>(null)
    const [messageApi, contextHolder] = message.useMessage()
    const { mutateAsync: generateShareableLink } = useGenerateShareableLinkMutation(documentId!, previewImage)
    const { data: shareableLink } = useGetShareableLinkQuery(moneyflow?.publicLink)
    const { token } = theme.useToken()
    const { darkModeEnabled } = useDarkMode()

    const captureScreenshot = useCallback(async () => {
        let imageFile: File = new File([], '', { type: 'image/png' })
        try {
            if (htmlContainer) {
                const canvas = await html2canvas(htmlContainer, {
                    backgroundColor: darkModeEnabled ? '#1f1f1f' : '#ffffff',
                })
                await addBranding(canvas.getContext('2d')!, canvas)
                canvas.toBlob(async (blob) => {
                    if (!blob || !documentId) return

                    imageFile = new File([blob], `moneyflow-${documentId}.png`, { type: 'image/png' })
                    setPreviewImage(imageFile)
                })
            }
        } catch (error) {
            console.error('Failed to capture screenshot', error)
            message.error('Failed to capture screenshot')
        }
    }, [documentId, htmlContainer, darkModeEnabled])

    const handleShareClick: FormEventHandler<HTMLFormElement> = useCallback(
        async (e) => {
            e.preventDefault()
            let link = shareableLink

            link = await generateShareableLink(name)
            refetch()

            const url = `${window.location.origin}/${link.slug}`

            setTimeout(
                () =>
                    navigator.clipboard
                        .writeText(url)
                        .then(() => {
                            messageApi.success('Public Link to MoneyFlow copied to clipboard')
                        })
                        .catch((e) => {
                            console.error('Failed to copy link', e)
                            messageApi.error('Failed to copy link')
                        }),
                0
            )
        },
        [shareableLink, generateShareableLink, name, refetch, messageApi]
    )

    useEffect(() => {
        if (visible && !shareableLink) {
            setTimeout(() => captureScreenshot(), 100)
        }
    }, [visible, captureScreenshot, shareableLink])

    return (
        <Modal
            title={
                <Typography.Title style={{ margin: 0, paddingBottom: token.paddingSM }} level={5}>
                    Share {moneyflow?.name}
                </Typography.Title>
            }
            width={640}
            open={visible}
            onCancel={() => hide()}
            footer={null}
            centered
            styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
        >
            You are going to share the MoneyFlow, this will generate a public link that can be accessed by anyone. Only
            the MoneyFlow data will be shared, no other information will be shared.
            <Typography.Title level={5}>Preview</Typography.Title>
            {previewImage || shareableLink ? (
                <div
                    style={{
                        width: '100%',
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadiusLG,
                        overflow: 'hidden',
                    }}
                >
                    <img
                        src={previewImage ? URL.createObjectURL(previewImage) : '/public/get-preview/' + moneyflow?._id}
                        alt='MoneyFlow Preview'
                        style={{ width: '100%', maxHeight: '45vh', objectFit: 'contain', display: 'block' }}
                    />
                </div>
            ) : (
                <Flex
                    justify='center'
                    align='center'
                    style={{
                        height: 200,
                        background: token.colorBgContainerDisabled,
                        marginBottom: token.marginLG,
                        borderRadius: token.borderRadiusLG,
                    }}
                >
                    <Typography.Text type='secondary'>Loading preview...</Typography.Text>
                </Flex>
            )}
            <Typography.Text style={{ display: 'block', marginBottom: token.marginMD }}>
                You can adjust the view on the grid page, and this preview will update accordingly when you re-open this
                sharing dialog.
            </Typography.Text>
            <Typography.Text>Name:</Typography.Text>
            <form
                onSubmit={handleShareClick}
                style={{ marginBottom: '8px', display: 'flex', gap: token.margin, alignItems: 'center' }}
            >
                <Input
                    disabled={!!shareableLink}
                    value={shareableLink ? shareableLink.name : name}
                    onChange={(e) => setName(e.target.value)}
                />
                <Button
                    disabled={!!shareableLink}
                    type='primary'
                    htmlType='submit'
                    icon={<ShareAltOutlined />}
                    iconPosition='end'
                >
                    Share
                </Button>
            </form>
            {shareableLink && (
                <div>
                    <Typography.Text>Public link:</Typography.Text>{' '}
                    <a
                        href={`${window.location.origin}/${shareableLink.slug}`}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {window.location.origin}/{shareableLink.slug}
                    </a>
                </div>
            )}
            {contextHolder}
        </Modal>
    )
}
